import { DAY_LABELS, PRODUCT_METADATA } from './settingsConstants';

export const buildClientKey = () => `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export const createEmptyAddress = (nome = '') => ({
  client_key: buildClientKey(),
  id: '',
  nome,
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: '',
  latitude: '',
  longitude: '',
  // Mantido no contrato para não apagar configurações legadas do Clube de Mesa.
  raio_metros: '80',
});

export const normalizeAddress = (address, fallbackName = '') => ({
  client_key: address?.client_key || (address?.id ? `id_${address.id}` : buildClientKey()),
  id: address?.id || '',
  nome: address?.nome || fallbackName,
  logradouro: address?.logradouro || '',
  numero: address?.numero || '',
  complemento: address?.complemento || '',
  bairro: address?.bairro || '',
  cidade: address?.cidade || '',
  estado: address?.estado || '',
  cep: address?.cep || '',
  latitude: address?.latitude ?? '',
  longitude: address?.longitude ?? '',
  raio_metros: address?.raio_metros != null && address.raio_metros !== ''
    ? String(address.raio_metros)
    : '80',
});

export const normalizeScheduleDays = (days) => {
  const source = Array.isArray(days) ? days : [];
  const normalized = DAY_LABELS.map((label, dayIndex) => {
    const found = source.find((item) => Number(item?.day_index ?? item?.dayIndex) === dayIndex);
    const closed = Boolean(found?.closed ?? true);
    const periods = Array.isArray(found?.periods)
      ? found.periods
        .map((period) => ({ start: period?.start?.slice?.(0, 5) || '', end: period?.end?.slice?.(0, 5) || '' }))
        .filter((period) => period.start || period.end)
      : [];
    return {
      day_index: dayIndex,
      day_label: found?.day_label || label,
      closed,
      periods: closed ? [] : (periods.length ? periods : [{ start: '', end: '' }]),
    };
  });
  normalized.forEach((day, dayIndex) => {
    if (day.closed) return;
    const nextDay = normalized[(dayIndex + 1) % normalized.length];
    const overnightStartIndex = day.periods.findIndex((period) => period.end === '23:59');
    const overnightEndIndex = nextDay?.periods?.findIndex((period) => period.start === '00:00') ?? -1;
    if (overnightStartIndex < 0 || overnightEndIndex < 0) return;
    day.periods[overnightStartIndex] = {
      ...day.periods[overnightStartIndex],
      end: nextDay.periods[overnightEndIndex].end,
    };
    nextDay.periods.splice(overnightEndIndex, 1);
    if (nextDay.periods.length === 0) nextDay.closed = true;
  });
  return normalized;
};

export const cloneScheduleDays = (days) => normalizeScheduleDays(days).map((day) => ({
  ...day,
  periods: day.periods.map((period) => ({ ...period })),
}));

export const buildUnitSchedules = ({ principalAddress, additionalAddresses, existing = [], fallback = [] }) => {
  const existingById = new Map(existing.filter((unit) => unit?.address_id).map((unit) => [String(unit.address_id), unit]));
  const principalExisting = existing.find((unit) => unit?.is_principal);
  const principalDays = cloneScheduleDays(principalExisting?.schedule_days || fallback);
  const principal = {
    address_key: 'principal',
    address_id: principalAddress?.id || '',
    address_nome: principalAddress?.nome || 'Unidade principal',
    profile_id: principalExisting?.profile_id || '',
    is_principal: true,
    use_principal_schedule: true,
    schedule_days: principalDays,
  };
  const others = additionalAddresses.map((address, index) => {
    const match = (address.id && existingById.get(String(address.id)))
      || existing.find((unit) => unit?.address_key === address.client_key)
      || existing.find((unit) => !unit?.is_principal && unit?.address_nome === address.nome);
    const inherits = match ? Boolean(match.use_principal_schedule ?? true) : true;
    return {
      address_key: address.client_key,
      address_id: address.id || '',
      address_nome: address.nome || `Unidade ${index + 1}`,
      profile_id: match?.profile_id || '',
      is_principal: false,
      use_principal_schedule: inherits,
      schedule_days: inherits ? cloneScheduleDays(principalDays) : cloneScheduleDays(match?.schedule_days || []),
    };
  });
  return [principal, ...others];
};

export const normalizeEditorResponse = (data = {}) => {
  const principalAddress = normalizeAddress(data.principal_address, 'Unidade principal');
  const additionalAddresses = Array.isArray(data.additional_addresses)
    ? data.additional_addresses.map((item, index) => normalizeAddress(item, `Unidade ${index + 1}`))
    : [];
  const existing = Array.isArray(data.unit_schedules)
    ? data.unit_schedules.map((unit) => ({
      address_key: unit?.is_principal ? 'principal' : (unit?.address_id ? `id_${unit.address_id}` : buildClientKey()),
      address_id: unit?.address_id || '',
      address_nome: unit?.address_nome || '',
      profile_id: unit?.profile_id || '',
      is_principal: Boolean(unit?.is_principal),
      use_principal_schedule: Boolean(unit?.use_principal_schedule ?? true),
      schedule_days: normalizeScheduleDays(unit?.schedule_days || []),
    }))
    : [];
  const unitSchedules = buildUnitSchedules({
    principalAddress,
    additionalAddresses,
    existing,
    fallback: normalizeScheduleDays(data.schedule_days || []),
  });
  return { principalAddress, additionalAddresses, unitSchedules };
};

export const hasAddressSetup = (address = {}) => [
  address.logradouro, address.numero, address.bairro, address.cidade, address.estado, address.cep,
].some((value) => Boolean(String(value || '').trim()));

export const hasScheduleSetup = (units = []) => units.some((unit) =>
  normalizeScheduleDays(unit.schedule_days).some((day) =>
    !day.closed && day.periods.some((period) => period.start && period.end)));

const timeToMinutes = (value) => {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(String(value || ''))) return null;
  const [hour, minute] = value.split(':').map(Number);
  return (hour * 60) + minute;
};

export const validateAddress = (address, label = 'Unidade') => {
  const cep = String(address?.cep || '').replace(/\D/g, '');
  if (cep && cep.length !== 8) return `${label}: informe um CEP válido com 8 dígitos.`;
  const uf = String(address?.estado || '').trim().toUpperCase();
  if (uf && !/^[A-Z]{2}$/.test(uf)) return `${label}: informe o estado com 2 letras.`;
  return null;
};

export const validateSchedule = (days, label = 'Horários') => {
  const normalized = normalizeScheduleDays(days);
  const timeline = DAY_LABELS.map(() => []);
  for (const day of normalized) {
    if (day.closed) continue;
    if (!day.periods.length) return `${label}: adicione um intervalo de funcionamento em ${day.day_label}.`;
    const seen = new Set();
    for (const period of day.periods) {
      const start = timeToMinutes(period.start);
      const end = timeToMinutes(period.end);
      if (start == null || end == null) return `${label}: preencha horários válidos em ${day.day_label}.`;
      if (start === end) return `${label}: abertura e fechamento devem ser diferentes em ${day.day_label}.`;
      const key = `${start}-${end}`;
      if (seen.has(key)) return `${label}: há intervalos duplicados em ${day.day_label}.`;
      seen.add(key);
      if (end > start) timeline[day.day_index].push({ start, end });
      else {
        timeline[day.day_index].push({ start, end: 1440 });
        if (end > 0) timeline[(day.day_index + 1) % 7].push({ start: 0, end });
      }
    }
  }
  for (let index = 0; index < timeline.length; index += 1) {
    const intervals = timeline[index].sort((a, b) => a.start - b.start);
    for (let cursor = 1; cursor < intervals.length; cursor += 1) {
      if (intervals[cursor].start < intervals[cursor - 1].end) {
        return `${label}: há intervalos sobrepostos em ${DAY_LABELS[index]}.`;
      }
    }
  }
  return null;
};

export const formatAddress = (address = {}) => {
  const first = [address.logradouro, address.numero].filter(Boolean).join(', ');
  const second = [address.bairro, address.cidade, address.estado].filter(Boolean).join(' · ');
  return [first, second, address.cep].filter(Boolean).join(' — ') || 'Endereço ainda não informado';
};

export const formatScheduleSummary = (days = []) => {
  const open = normalizeScheduleDays(days).filter((day) => !day.closed && day.periods.length);
  if (!open.length) return 'Horários ainda não definidos';
  const today = open.find((day) => day.day_index === new Date().getDay());
  const target = today || open[0];
  return `${target.day_label}: ${target.periods.map((period) => `${period.start}–${period.end}`).join(', ')}`;
};

export const getCurrentScheduleStatus = (days = [], now = new Date()) => {
  const normalized = normalizeScheduleDays(days);
  const todayIndex = now.getDay();
  const minutes = (now.getHours() * 60) + now.getMinutes();
  const today = normalized[todayIndex];
  const previous = normalized[(todayIndex + 6) % 7];
  const previousOvernight = previous.periods.find((period) => {
    const start = timeToMinutes(period.start);
    const end = timeToMinutes(period.end);
    return !previous.closed && start != null && end != null && end < start && minutes < end;
  });
  if (previousOvernight) return { open: true, label: `Aberta agora · até ${previousOvernight.end}` };
  const current = today.periods.find((period) => {
    const start = timeToMinutes(period.start);
    const end = timeToMinutes(period.end);
    if (today.closed || start == null || end == null) return false;
    return end > start ? minutes >= start && minutes < end : minutes >= start;
  });
  if (current) return { open: true, label: `Aberta agora · até ${current.end}` };
  for (let offset = 0; offset < 7; offset += 1) {
    const day = normalized[(todayIndex + offset) % 7];
    if (day.closed) continue;
    const starts = day.periods
      .map((period) => period.start)
      .filter((value) => timeToMinutes(value) != null)
      .sort();
    const next = offset === 0 ? starts.find((value) => timeToMinutes(value) > minutes) : starts[0];
    if (next) return { open: false, label: offset === 0 ? `Fechada agora · abre hoje às ${next}` : `Fechada agora · abre ${day.day_label.toLowerCase()} às ${next}` };
  }
  return { open: false, label: 'Fechada · sem próximo horário definido' };
};

export const getProductView = (rule) => {
  const metadata = PRODUCT_METADATA[rule?.id] || {};
  return {
    ...metadata,
    id: rule?.id,
    name: metadata.name || rule?.label || rule?.id || 'Produto',
    description: metadata.description || rule?.description || 'Configuração do produto',
    usePrincipal: Boolean(rule?.usar_regra_global),
  };
};

export const stableStringify = (value) => JSON.stringify(value);

export const geocodeToPatch = (result = {}, current = {}) => ({
  logradouro: result.street || current.logradouro || '',
  numero: result.number || current.numero || '',
  bairro: result.neighborhood || current.bairro || '',
  cidade: result.city || current.cidade || '',
  estado: result.state || current.estado || '',
  cep: result.postal_code || current.cep || '',
  latitude: result.lat != null ? String(result.lat) : current.latitude || '',
  longitude: result.long != null ? String(result.long) : current.longitude || '',
});
